import type { ProductColumnId, ProductSummary } from '@aeris/contracts'
import { UNIT_PRICE_SCALE, fromString, toString } from '@aeris/money'
import { marginFromPrice } from '@aeris/pricing'

import {
  dateValue,
  numberValue,
  textValue,
  type ExportColumn,
} from './export/table-export.ts'
import { CONDITION_LABEL } from './product-columns.tsx'

/**
 * O que cada coluna de Produtos põe no arquivo exportado — F131.
 *
 * Um `Record` com todas as colunas, e não um campo opcional em cada uma: uma
 * coluna nova na lista que ninguém lembrou de exportar vira erro de tipo, e não
 * uma coluna que some da planilha sem aviso. Uma coluna da tela pode virar mais
 * de uma no arquivo (a foto vira o endereço dela) ou nenhuma.
 *
 * O arquivo leva o que a pessoa já vê: a rota da lista não entrega preço sem
 * `pricing.read` nem custo sem `catalog.write`, e a exportação lê a mesma rota.
 */

type Column = ExportColumn<ProductSummary>

const variant = (product: ProductSummary) =>
  product.variants.find((entry) => entry.isDefault) ?? product.variants[0]

export const PRODUCT_EXPORT: Record<ProductColumnId, readonly Column[]> = {
  photo: [
    {
      id: 'photoUrl',
      label: 'Foto',
      value: (product) => textValue(product.primaryImage?.fullUrl),
    },
  ],
  category: [
    {
      id: 'category',
      label: 'Categoria',
      value: (product) => textValue(product.categoryName),
    },
  ],
  sku: [
    {
      id: 'sku',
      label: 'Código',
      value: (product) => textValue(variant(product)?.sku),
    },
  ],
  name: [{ id: 'name', label: 'Nome', value: (product) => product.name }],
  brand: [
    {
      id: 'brand',
      label: 'Marca',
      value: (product) => textValue(product.brand),
    },
  ],
  unit: [
    {
      id: 'unit',
      label: 'Unidade',
      value: (product) => textValue(variant(product)?.unit),
    },
  ],
  gtin: [
    {
      id: 'gtin',
      label: 'Código de barras',
      value: (product) => textValue(variant(product)?.gtin),
    },
  ],
  ncm: [
    {
      id: 'ncm',
      label: 'NCM',
      value: (product) => textValue(variant(product)?.ncm),
    },
  ],
  supplierReference: [
    {
      id: 'supplierReference',
      label: 'Fornecedor',
      value: (product) => textValue(variant(product)?.supplierReference),
    },
  ],
  location: [
    {
      id: 'location',
      label: 'Localização',
      value: (product) => textValue(product.location),
    },
  ],
  price: [
    {
      id: 'price',
      label: 'Preço',
      value: (product) => numberValue(product.price?.basePrice),
    },
  ],
  cost: [
    {
      id: 'averageCost',
      label: 'Custo médio',
      value: (product) => numberValue(product.cost?.averageCost),
    },
  ],
  margin: [
    {
      id: 'marginPercent',
      label: 'Margem (%)',
      value: (product) => {
        const cost = product.cost?.averageCost
        const price = product.price?.basePrice
        if (cost == null || price == null) return null
        const margin = marginFromPrice(
          fromString(cost, UNIT_PRICE_SCALE),
          fromString(price, UNIT_PRICE_SCALE)
        )
        return margin === null ? null : numberValue(toString(margin))
      },
    },
  ],
  stock: [
    {
      id: 'onHand',
      label: 'Estoque',
      value: (product) =>
        !product.tracksStock
          ? 'não controla'
          : numberValue(product.stock?.onHand),
    },
  ],
  available: [
    {
      id: 'available',
      label: 'Disponível',
      value: (product) =>
        product.tracksStock ? numberValue(product.stock?.available) : null,
    },
  ],
  lastPurchaseAt: [
    {
      id: 'lastPurchaseAt',
      label: 'Última compra',
      value: (product) => dateValue(product.cost?.lastPurchaseAt),
    },
  ],
  priceChangedAt: [
    {
      id: 'priceChangedAt',
      label: 'Preço alterado em',
      value: (product) => dateValue(product.priceChangedAt),
    },
  ],
  condition: [
    {
      id: 'condition',
      label: 'Condição',
      value: (product) => CONDITION_LABEL[product.condition],
    },
  ],
  warranty: [
    {
      id: 'warrantyMonths',
      label: 'Garantia (meses)',
      value: (product) =>
        product.warrantyMonths === null
          ? null
          : { number: String(product.warrantyMonths) },
    },
    {
      id: 'warrantyNote',
      label: 'Garantia (observação)',
      value: (product) => textValue(product.warrantyNote),
    },
  ],
  weight: [
    {
      id: 'weightGrams',
      label: 'Peso (g)',
      value: (product) =>
        product.weightGrams === null
          ? null
          : { number: String(product.weightGrams) },
    },
  ],
  status: [
    {
      id: 'status',
      label: 'Situação',
      value: (product) =>
        product.status === 'archived' ? 'Arquivado' : 'Ativo',
    },
  ],
  createdAt: [
    {
      id: 'createdAt',
      label: 'Cadastrado em',
      value: (product) => dateValue(product.createdAt),
    },
  ],
  updatedAt: [
    {
      id: 'updatedAt',
      label: 'Alterado em',
      value: (product) => dateValue(product.updatedAt),
    },
  ],
}

/** As colunas do arquivo, na ordem das que estão na tela. */
export const productExportColumns = (
  shown: readonly { readonly id: ProductColumnId }[]
): Column[] => shown.flatMap((column) => PRODUCT_EXPORT[column.id])
